import { SearchOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import classNames from "classnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import HBIcon from "../../components/HBIcon";
import { isEnter } from "../../utils/keyBoard";
import styles from "./search.module.less";

/**
 * 花瓣内容搜索输入框
 * @param {Omit<import("antd").InputProps, "placeholder"> & {
 *  hideSearchBtn?: boolean,
 *  onSearch?: function,
 *  placeholderCenter?: boolean,
 *  preTags?: Array,
 *  allowClear?: boolean,
 *  onClear?: function,
 *  placeholder?: string|string[],
 *  placeholderPrefix?: string,
 * }} props
 * @returns {JSX.Element}
 */
function HBSearch(
  {
    hideSearchBtn = false,
    onSearch,
    placeholderCenter = false,
    preTags = [],
    allowClear = true,
    onClear,
    placeholderPrefix = "",
    ...props
  } = {},
  ref
) {
  const [isFocus, setFocus] = useState(false);

  const [isFilled, setFilled] = useState(false);

  // 当前拼音输入的参数
  const [pinyininput, setPinyin] = useState(false);

  const preTagsRef = useRef(null);
  const [preTagsOffset, setPretagsOffset] = useState(0);

  const inputRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      input: inputRef.current.input,
      getCurrentPlaceholder: () => {
        return {
          value: Array.isArray(props.placeholder)
            ? props.placeholder[placeholderIndex.current]
            : props.placeholder,
          index: placeholderIndex.current,
        };
      },
    }),
    [props.placeholder]
  );

  useEffect(() => {
    if (props.value) {
      setFilled(true);
    } else {
      setFilled(false);
    }
  }, [props.value]);

  const tags = useMemo(() => {
    return preTags.filter((tag) => tag.label);
  }, [preTags]);

  useEffect(() => {
    if (tags && tags.length && preTagsRef.current) {
      setPretagsOffset(preTagsRef.current.offsetWidth - 10);
    } else {
      setPretagsOffset(0);
    }
  }, [tags]);

  const preTagClick = useCallback(() => {
    inputRef.current?.input?.focus();
  }, []);

  const prefix = tags.length ? null : <SearchOutlined />;
  const suffix =
    hideSearchBtn && !allowClear && !tags.length ? null : (
      <>
        {((isFilled || tags?.length) && allowClear && (
          <span className={styles.clear}>
            <HBIcon
              type="ic_close"
              onClick={() => {
                onClear && onClear();
              }}
            />
          </span>
        )) ||
          null}

        {!hideSearchBtn && (
          <Button
            className={styles.btn}
            type="primary"
            onClick={(e) => {
              inputRef.current.input.blur();
              onSearch && onSearch(inputRef.current.input.value);
              e.stopPropagation();
            }}
          >
            <SearchOutlined style={{ fontSize: "20px" }} />
          </Button>
        )}
      </>
    );

  // 轮播占位词dom
  const placeholderRef = useRef();
  // 当前轮播占位词索引
  const placeholderIndex = useRef(0);
  // 轮播占位词
  useEffect(() => {
    const placeholder = props.placeholder;
    let timer;
    function loop() {
      timer = setTimeout(() => {
        const height = placeholderRef.current.getBoundingClientRect().height;
        placeholderIndex.current++;
        if (placeholderIndex.current >= placeholder.length) {
          placeholderIndex.current = 0;
        }
        placeholderRef.current.scrollTo({
          top: height * placeholderIndex.current,
          behavior: "smooth",
        });

        loop();
      }, 5000);
    }
    if (
      placeholder &&
      Array.isArray(placeholder) &&
      placeholder.length > 1 &&
      !props.value
    ) {
      loop();
      return () => {
        clearTimeout(timer);
      };
    }
  }, [props.placeholder, props.value]);

  return (
    <div
      className={classNames(styles.wrap, {
        [styles.preTags]: tags?.length,
        [styles.filled]: isFilled,
        [styles.focus]: isFocus,
      })}
      style={props.style || {}}
    >
      {
        // 搜索范围标签
        tags.length > 0 && (
          <div className={styles.preTages} ref={preTagsRef}>
            {tags.map((tag) => {
              if (tag) {
                return (
                  <span
                    key={tag.label}
                    className={styles.tag}
                    onClick={preTagClick}
                  >
                    {tag.label}
                    <HBIcon
                      type="ic_close"
                      onClick={() => {
                        tag.clear(null);
                      }}
                    />
                  </span>
                );
              }
            })}
          </div>
        )
      }

      <div
        ref={placeholderRef}
        className={classNames(styles.placeholder, {
          [styles.center]: placeholderCenter,
        })}
        style={{
          transform: preTagsOffset ? `translateX(${preTagsOffset - 26}px)` : "",
        }}
      >
        {Array.isArray(props.placeholder) ? (
          props.placeholder.map((item, index) => {
            return (
              <div className={styles.item} key={index}>
                {placeholderPrefix + item}
              </div>
            );
          })
        ) : (
          <div className={styles.item}>{props.placeholder || "搜索"}</div>
        )}
      </div>

      <Input
        ref={inputRef}
        prefix={prefix}
        suffix={suffix}
        {...props}
        placeholder=""
        className={classNames(styles.search, {
          [styles.hasSearchBtn]: !hideSearchBtn,
        })}
        style={{ textIndent: `${preTagsOffset}px` }}
        autoComplete="off"
        onFocus={(e) => {
          setFocus(true);
          props.onFocus && props.onFocus(e);
        }}
        onBlur={(e) => {
          setFocus(false);
          if (e.target.value) {
            setFilled(true);
          } else {
            setFilled(false);
          }
          props.onBlur && props.onBlur(e);
        }}
        onKeyDown={(e) => {
          if (
            isEnter(e) &&
            !pinyininput &&
            // 360浏览器下搜狗中文输入法下，onCompositionStart不会触发，key为Process，所以需要在这里判断
            e.key !== "Process"
          ) {
            onSearch && onSearch(e.target.value);
            e.target.blur();
          }
          props.onKeyDown && props.onKeyDown(e);
        }}
        onInput={(e) => {
          if (e.target.value) {
            setFilled(true);
          } else {
            setFilled(false);
          }
          props.onInput && props.onInput(e);
        }}
        onCompositionStart={() => setPinyin(true)}
        onCompositionEnd={() =>
          // 没有异步在火狐浏览器里无法输入中文
          setTimeout(() => {
            setPinyin(false);
          }, 1)
        }
      />
    </div>
  );
}

export default forwardRef(HBSearch);
